import React, { FormEvent, useEffect, useState, useContext } from 'react'
import { useLocation } from '@reach/router'
import { graphql, navigate, useStaticQuery } from 'gatsby'
import { Head, Modal } from '@components'
import InputMask from 'react-input-mask'
import cn from 'classnames'
import axios from 'axios'
import * as st from '@assets/styl/contact.module.styl'
import { useLanguage } from '../../hooks'
import { translatePath } from '../../translations'
import { useTrafficSource } from '../../hooks/useTrafficSource'
import { context } from '../../layouts/index'
import { StaticImage } from 'gatsby-plugin-image'

type FormProps = {
  open?: boolean
  close: () => void
  states?: Array<{
    name: string
    cities: Array<{
      name: string
    }>
  }>
}

const OmbudsmanModal = ({ open, close }: FormProps) => {
  const { t } = useLanguage()
  const [submitLabel, setSubmitLabel] = useState(t('Contact.send'))
  const trafficSource = useTrafficSource()
  const defaultData = {
    nome: '',
    email: '',
    cpf: '',
    telefone: '',
    assunto: '',
    mensagem: '',
    permissao: false,
    identificador: 'Site - Ouvidoria',
  }
  const [data, setData] = useState({ ...defaultData })
  const onSubmit = (
    e: FormEvent,
    data: any,
    loadingHandler: () => void,
    successHandler: () => void
  ) => {
    e.preventDefault()
    data.token_rdstation = '529cac2f361142d6e1232668a5dbe8f9'
    data.traffic_source = trafficSource
    loadingHandler()
    axios
      .post('https://www.rdstation.com.br/api/1.3/conversions', data, {
        headers: {
          'Content-type': 'application/json',
        },
      })
      .then((data) => {
        loadingHandler()
        data.status === 200 && navigate('/sucesso/')
        successHandler()
      })
  }

  return (
    <Modal open={open} close={close} className={st.modal} noCloseButton>
      <button onClick={close} className={st.close}></button>
      <h3>{t('Contact.ombudsman')}</h3>
      <form
        onSubmit={(e) =>
          onSubmit(
            e,
            data,
            () =>
              setSubmitLabel(
                submitLabel !== t('Contact.send')
                  ? t('Contact.send')
                  : t('Contact.loading')
              ),
            () => {
              setData({ ...defaultData })
              close()
            }
          )
        }
      >
        <div>
          <label htmlFor="Ouvidoria-nome">{t('Contact.form.name')}</label>
          <input
            type="text"
            id="Ouvidoria-nome"
            value={data.nome}
            onChange={(e) => setData({ ...data, nome: e.target.value })}
            required
          />
        </div>
        <div>
          <label htmlFor="Ouvidoria-email">{t('Contact.form.email')}</label>
          <input
            type="email"
            id="Ouvidoria-email"
            value={data.email}
            onChange={(e) => setData({ ...data, email: e.target.value })}
            required
          />
        </div>
        <div className={st.sideInput}>
          <div className={st.small}>
            <label htmlFor="Ouvidoria-cpf">CPF</label>
            <InputMask
              mask="999.999.999-99"
              maskChar={null}
              type="text"
              id="Ouvidoria-cpf"
              value={data.cpf}
              onChange={(e) => setData({ ...data, cpf: e.target.value })}
              required
              pattern=".{14,14}"
            />
          </div>
          <div className={cn(st.small, st.smallLast)}>
            <label htmlFor="Ouvidoria-telefone">
              {t('Contact.form.telephone')}
            </label>
            <InputMask
              mask={
                data.telefone.length > 14
                  ? '(99) 99999-9999'
                  : '(99) 9999-99999'
              }
              maskChar={null}
              type="tel"
              id="Ouvidoria-telefone"
              value={data.telefone}
              onChange={(e) => setData({ ...data, telefone: e.target.value })}
              placeholder={t('Contact.form.placeholderTelephone')}
              required
              pattern=".{14,15}"
            />
          </div>
        </div>
        <div>
          <label htmlFor="Ouvidoria-assunto">{t('Contact.form.subject')}</label>
          <input
            type="text"
            id="Ouvidoria-assunto"
            value={data.assunto}
            onChange={(e) => setData({ ...data, assunto: e.target.value })}
            required
          />
        </div>
        <div>
          <label htmlFor="Ouvidoria-mensagem">
            {t('Contact.form.message')}
          </label>
          <textarea
            id="Ouvidoria-mensagem"
            value={data.mensagem}
            onChange={(e) => setData({ ...data, mensagem: e.target.value })}
            required
          ></textarea>
        </div>
        <div>
          <div className={st.checkbox}>
            <div className={cn(data.permissao && st.checked)}>
              <input
                type="checkbox"
                id="Ouvidoria-permissao"
                checked={data.permissao}
                onChange={(e) =>
                  setData({ ...data, permissao: e.target.checked })
                }
                required
              />
            </div>
            <label htmlFor="Ouvidoria-permissao">
              <p>
                {t('Contact.terms.1')}{' '}
                <a
                  target="_blank"
                  href="https://www.dimasconstrucoes.com.br/politica-de-privacidade/"
                >
                  {t('Contact.terms.2')}
                </a>{' '}
                {t('Contact.terms.3')}{' '}
                <a
                  target="_blank"
                  href="https://www.dimasconstrucoes.com.br/termos-de-uso/"
                >
                  {t('Contact.terms.4')}
                </a>
                .
              </p>
            </label>
          </div>
        </div>
        <div>
          <button className="btn">{submitLabel}</button>
        </div>
      </form>
    </Modal>
  )
}

const ProvidersModal = ({ open, close, states }: FormProps) => {
  const { t } = useLanguage()
  const [submitLabel, setSubmitLabel] = useState(t('Contact.send'))
  const trafficSource = useTrafficSource()
  const defaultData = {
    empresa: '',
    responsavel: '',
    email: '',
    telefone: '',
    estado: '',
    cidade: '',
    site: '',
    resumo: '',
    diferenciais: '',
    produtos_servicos: '',
    permissao: false,
    identificador: 'Site - Fornecedores',
  }
  const [data, setData] = useState({ ...defaultData })
  const onSubmit = (
    e: FormEvent,
    data: any,
    loadingHandler: () => void,
    successHandler: () => void
  ) => {
    e.preventDefault()
    data.token_rdstation = '529cac2f361142d6e1232668a5dbe8f9'
    data.traffic_source = trafficSource
    loadingHandler()
    axios
      .post('https://www.rdstation.com.br/api/1.3/conversions', data, {
        headers: {
          'Content-type': 'application/json',
        },
      })
      .then((data) => {
        loadingHandler()
        data.status === 200 && navigate('/sucesso/')
        successHandler()
      })
  }
  return (
    <Modal open={open} close={close} className={st.modal} noCloseButton>
      <button onClick={close} className={st.close}></button>
      <h3>{t('Contact.suppliers')}</h3>
      <form
        onSubmit={(e) =>
          onSubmit(
            e,
            data,
            () =>
              setSubmitLabel(
                submitLabel !== t('Contact.send')
                  ? t('Contact.send')
                  : t('Contact.loading')
              ),
            () => {
              setData({ ...defaultData })
              close()
            }
          )
        }
      >
        <div>
          <label htmlFor="Fornecedores-empresa">
            {t('Contact.companyName')}
          </label>
          <input
            type="text"
            id="Fornecedores-empresa"
            value={data.empresa}
            onChange={(e) => setData({ ...data, empresa: e.target.value })}
            required
          />
        </div>
        <div>
          <label htmlFor="Fornecedores-responsavel">
            {t('Contact.nameOfPerson')}
          </label>
          <input
            type="text"
            id="Fornecedores-responsavel"
            value={data.responsavel}
            onChange={(e) => setData({ ...data, responsavel: e.target.value })}
            required
          />
        </div>
        <div>
          <label htmlFor="Fornecedores-email">{t('Contact.email')}</label>
          <input
            type="email"
            id="Fornecedores-email"
            value={data.email}
            onChange={(e) => setData({ ...data, email: e.target.value })}
            required
          />
        </div>
        <div>
          <label htmlFor="Fornecedores-telefone">
            {t('Contact.telephone')}
          </label>
          <InputMask
            mask={
              data.telefone.length > 14 ? '(99) 99999-9999' : '(99) 9999-99999'
            }
            maskChar={null}
            type="tel"
            id="Fornecedores-telefone"
            value={data.telefone}
            onChange={(e) => setData({ ...data, telefone: e.target.value })}
            placeholder={t('Contact.form.placeholderTelephone')}
            required
            pattern=".{14,15}"
          />
        </div>
        <div className={st.sideInput}>
          <div className={st.small}>
            <label htmlFor="Fornecedores-estado">{t('Contact.state')}</label>
            <select
              id="Fornecedores-estado"
              value={data.estado}
              onChange={(e) => setData({ ...data, estado: e.target.value })}
              required
            >
              <option disabled hidden value="">
                {t('Contact.select')}
              </option>
              {states.map((state, key) => (
                <option key={key}>{state.name}</option>
              ))}
            </select>
          </div>
          <div className={cn(st.small, st.smallLast)}>
            <label htmlFor="Fornecedores-cidade">{t('Contact.city')}</label>
            <select
              id="Fornecedores-cidade"
              value={data.cidade}
              onChange={(e) => setData({ ...data, cidade: e.target.value })}
              required
              disabled={!data.estado}
            >
              <option disabled hidden value="">
                {t('Contact.select')}
              </option>
              {Boolean(data.estado) &&
                states
                  .filter((s) => s.name === data.estado)[0]
                  .cities.map((city, key) => (
                    <option key={key}>{city.name}</option>
                  ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="Fornecedores-site">{t('Contact.site')}</label>
          <input
            type="text"
            id="Fornecedores-site"
            value={data.site}
            onChange={(e) => setData({ ...data, site: e.target.value })}
            required
          />
        </div>
        <div>
          <label htmlFor="Fornecedores-resumo">{t('Contact.summary')}</label>
          <textarea
            id="Fornecedores-resumo"
            value={data.resumo}
            onChange={(e) => setData({ ...data, resumo: e.target.value })}
            required
          ></textarea>
        </div>
        <div>
          <label htmlFor="Fornecedores-diferenciais">
            {t('Contact.differences')}
          </label>
          <textarea
            id="Fornecedores-diferenciais"
            value={data.diferenciais}
            onChange={(e) => setData({ ...data, diferenciais: e.target.value })}
            required
          ></textarea>
        </div>
        <div>
          <label htmlFor="Fornecedores-produtos">
            {t('Contact.productsServices')}
          </label>
          <textarea
            id="Fornecedores-produtos"
            value={data.produtos_servicos}
            onChange={(e) =>
              setData({ ...data, produtos_servicos: e.target.value })
            }
            required
          ></textarea>
        </div>
        <div>
          <div className={st.checkbox}>
            <div className={cn(data.permissao && st.checked)}>
              <input
                type="checkbox"
                id="Fornecedores-permissao"
                checked={data.permissao}
                onChange={(e) =>
                  setData({ ...data, permissao: e.target.checked })
                }
                required
              />
            </div>
            <label htmlFor="Fornecedores-permissao">
              <p>
                {t('Contact.terms.1')}{' '}
                <a
                  target="_blank"
                  href="https://www.dimasconstrucoes.com.br/politica-de-privacidade/"
                >
                  {t('Contact.terms.2')}
                </a>{' '}
                {t('Contact.terms.3')}{' '}
                <a
                  target="_blank"
                  href="https://www.dimasconstrucoes.com.br/termos-de-uso/"
                >
                  {t('Contact.terms.4')}
                </a>
                .
              </p>
            </label>
          </div>
        </div>
        <div>
          <button className="btn">{submitLabel}</button>
        </div>
      </form>
    </Modal>
  )
}

const Contact = () => {
  const config = useStaticQuery(graphql`
    query {
      allAddresses {
        nodes {
          location
        }
      }
      allConfigurations {
        nodes {
          phone
          whatsapp
        }
      }
      allFormOptions {
        nodes {
          name
          reference
        }
      }
      allOptionContacts {
        nodes {
          name
          slug
        }
      }
      allStates {
        nodes {
          name
          cities {
            name
          }
        }
      }
    }
  `)
  const locationHook = useLocation()
  const location = config.allAddresses.nodes[0].location.split(/(.*?\d+ )/)
  const socials = config.allConfigurations.nodes[0]
  const formOptions = config.allFormOptions.nodes
  const optionContacts = config.allOptionContacts.nodes
  const states = config.allStates.nodes
  const { t } = useLanguage()
  const [submitLabel, setSubmitLabel] = useState(t('Contact.send'))
  const trafficSource = useTrafficSource()
  const defaultData = {
    assunto: '',
    nome: '',
    email: '',
    telefone: '',
    empreendimento: '',
    idempreendimento: '',
    mensagem: '',
    permissao: false,
    identificador: 'Site - Info e vendas',
  }
  const [data, setData] = useState({ ...defaultData })
  const { modalOpen, setModalOpen } = useContext(context)

  const scrollToSection = (id) => {
    const element = document.getElementById(id)

    if (element) {
      window.scrollTo({
        top: element.offsetTop,
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    if (locationHook.hash) {
      setTimeout(() => {
        scrollToSection(locationHook.hash.slice(1))
      }, 500)
    }
  }, [locationHook.hash])

  const onSubmit = (
    e: FormEvent,
    data: any,
    loadingHandler: () => void,
    successHandler: () => void
  ) => {
    e.preventDefault()
    data.token_rdstation = '529cac2f361142d6e1232668a5dbe8f9'
    data.traffic_source = trafficSource
    loadingHandler()
    axios
      .post('https://www.rdstation.com.br/api/1.3/conversions', data, {
        headers: {
          'Content-type': 'application/json',
        },
      })
      .then((data) => {
        loadingHandler()
        data.status === 200 && navigate('/sucesso/')
        successHandler()
      })
  }

  return (
    <>
      <Head
        title={t('Contact.titlePage')}
        pathname={translatePath('/contato/')}
        basePath="/contato/"
        description={t('Contact.descriptionPage')}
      />

      <section className={cn(st.hero, 'heroScroll')}>
        <h1>
          {t('Contact.titleTalkTeam.1')} <b>{t('Contact.titleTalkTeam.2')}</b>
        </h1>
        <div className={st.bg}>
          <StaticImage
            src="../../assets/img/sala-celular.jpg"
            alt=""
            className={st.desktop}
            loading="lazy"
          />
          <StaticImage
            src="../../assets/img/sala-celular-mobile.jpg"
            alt=""
            className={st.mobile}
            loading="lazy"
          />
        </div>
      </section>

      <section className={st.formWrapper}>
        <h2>{t('Contact.informationAndSales')}</h2>
        <div>
          <p>
            {t('Contact.requestMoreInformation')}
            <br />
            {t('Contact.greatBusiness')}
          </p>
          <form
            onSubmit={(e) =>
              onSubmit(
                e,
                data,
                () =>
                  setSubmitLabel(
                    submitLabel !== t('Contact.send')
                      ? t('Contact.send')
                      : t('Contact.loading')
                  ),
                () => setData({ ...defaultData })
              )
            }
          >
            <div className={st.wide}>
              <label className={st.subject}>{t('Contact.form.subject')}</label>
              <div className={st.radioGroup}>
                <div
                  className={cn(data.assunto === 'Informações' && st.checked)}
                >
                  <input
                    type="radio"
                    id="assunto-info"
                    name="assunto"
                    value="Informações"
                    onChange={(e) =>
                      setData({ ...data, assunto: e.target.value })
                    }
                    required
                  />
                </div>
                <label htmlFor="assunto-info" className={st.subject}>
                  {t('Contact.information')}
                </label>
                <div className={cn(data.assunto === 'Vendas' && st.checked)}>
                  <input
                    type="radio"
                    id="assunto-vendas"
                    name="assunto"
                    value="Vendas"
                    onChange={(e) =>
                      setData({ ...data, assunto: e.target.value })
                    }
                  />
                </div>
                <label htmlFor="assunto-vendas" className={st.subject}>
                  {t('Contact.sales')}
                </label>
              </div>
            </div>
            <div>
              <label htmlFor="nome">{t('Contact.form.name')}</label>
              <input
                type="text"
                id="nome"
                value={data.nome}
                onChange={(e) => setData({ ...data, nome: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="email">{t('Contact.form.email')}</label>
              <input
                type="email"
                id="email"
                value={data.email}
                onChange={(e) => setData({ ...data, email: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="telefone">{t('Contact.form.telephone')}</label>
              <InputMask
                mask={
                  data.telefone.length > 14
                    ? '(99) 99999-9999'
                    : '(99) 9999-99999'
                }
                maskChar={null}
                type="tel"
                id="telefone"
                placeholder={t('Contact.form.placeholderTelephone')}
                value={data.telefone}
                onChange={(e) => setData({ ...data, telefone: e.target.value })}
                required
                pattern=".{14,15}"
              />
            </div>
            <div>
              <label htmlFor="empreendimento">{t('Contact.enterprise')}</label>
              <select
                id="empreendimento"
                value={
                  data.empreendimento
                    ? `${data.empreendimento},${data.idempreendimento}`
                    : ''
                }
                onChange={(e) => {
                  const value = e.target.value.split(',')
                  setData({
                    ...data,
                    empreendimento: value[0],
                    idempreendimento: value[1],
                  })
                }}
                required
              >
                <option disabled hidden value="">
                  {t('Contact.select')}
                </option>
                {optionContacts.map((opt, key) => (
                  <option key={key} value={`${opt.name},${opt.reference}`}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={st.wide}>
              <label htmlFor="mensagem" className={st.wideText}>
                {t('Contact.form.message')}
              </label>
              <textarea
                value={data.mensagem}
                onChange={(e) => setData({ ...data, mensagem: e.target.value })}
                required
              ></textarea>
            </div>
            <div className={cn(st.checkbox, st.wide)}>
              <div className={cn(data.permissao && st.checked)}>
                <input
                  type="checkbox"
                  id="permissao"
                  checked={data.permissao}
                  onChange={(e) =>
                    setData({ ...data, permissao: e.target.checked })
                  }
                  required
                />
              </div>
              <label htmlFor="permissao">
                <p>
                  {t('Contact.terms.1')}{' '}
                  <a
                    target="_blank"
                    href="https://www.dimasconstrucoes.com.br/politica-de-privacidade/"
                  >
                    {t('Contact.terms.2')}
                  </a>{' '}
                  {t('Contact.terms.3')}{' '}
                  <a
                    target="_blank"
                    href="https://www.dimasconstrucoes.com.br/termos-de-uso/"
                  >
                    {t('Contact.terms.4')}
                  </a>
                  .
                </p>
              </label>
            </div>
            <div className={st.wide}>
              <button className="btn">{submitLabel}</button>
            </div>
          </form>
        </div>
      </section>

      <section className={st.phoneAddress}>
        <div className={st.titleContainer}>
          <h2>{t('Contact.titleContact')}</h2>
          <p>{t('Contact.descriptionContact')}</p>
        </div>
        <ul>
          <li>
            <div className={st.addressIcons}>
              <b className="icon-phone"></b>
              <h3>{t('Contact.form.telephone')}</h3>
            </div>
            <b>{socials.phone}</b>
          </li>
          <li>
            <a
              href={`https://api.whatsapp.com/send?phone=55${socials.whatsapp.replace(
                / /g,
                ''
              )}`}
              target="_blank"
            >
              <div className={st.addressIcons}>
                <b className="icon-whatsapp"></b>
                <h3>WhatsApp</h3>
              </div>
              <div className="btn-white icon-whatsapp green">
                {t('Contact.send')}
              </div>
            </a>
          </li>
          <li>
            <div className={st.addressIcons}>
              <b className="icon-location"></b>
              <h3>{t('Contact.Address')}</h3>
            </div>
            <span>
              {location[1]}
              <br />
              {location[2]}
            </span>
          </li>
        </ul>
      </section>

      <div className={st.ombudsman}>
        <div>
          <h2>{t('Contact.ombudsman')}</h2>
          <h3>
            {t('Contact.commitmentToSatisfaction')
              .split('\n')
              .map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index <
                    t('Contact.commitmentToSatisfaction').split('\n').length -
                      1 && <br />}
                </React.Fragment>
              ))}
          </h3>
          <p>{t('Contact.iso')}</p>
          <StaticImage
            src="../../assets/img/ouvidoria.jpg"
            alt="Homens apertando as mãos."
            loading="lazy"
            className={st.ombudsmanImage}
          />
          <button className="btn-white" onClick={() => setModalOpen(0)}>
            {t('Contact.send')}
          </button>
        </div>
      </div>

      <OmbudsmanModal close={() => setModalOpen(-1)} open={modalOpen === 0} />

      <section id="suppliers" className={st.providers}>
        <div>
          <div>
            <h2>{t('Contact.suppliers')}</h2>
            <p>
              {t('Contact.theDimas')}
              <br />
              <br />
              {t('Contact.weWantToAchieve')}
            </p>
            <button className="btn" onClick={() => setModalOpen(1)}>
              {t('Contact.send')}
            </button>
          </div>
          <div>
            <StaticImage
              src="../../assets/img/fornecedores.jpg"
              alt="Imagem de fornecedores"
              loading="lazy"
              className={st.providersImage}
            />
          </div>
        </div>
      </section>

      <ProvidersModal
        states={states}
        close={() => setModalOpen(-1)}
        open={modalOpen === 1}
      />

      {/*<WorkWithUsBanner />*/}
    </>
  )
}

export default Contact
